import React from 'react';

import { dataLayerProvider, tracker } from '@femtasy/helpers';
import { useIntl } from 'react-intl';

import Message from 'components/Message';
import Typography from 'components/Typography';

import ProgressBar from 'containers/Onboarding/ProgressBar';
import StepsBackButton from 'containers/Quiz/BackButton';
import QuizCard from 'containers/Quiz/Card';
import { useQuizContext } from 'containers/Quiz/hooks/useQuizContext';

import { IntlMsgShape } from 'helpers/consts';
import { isProduction } from 'helpers/env';

import StepPageViewWrapper from '../StepPageViewWrapper';
import './style.scss';

interface Choice {
  id: number;
  text: string;
  tracking: string;
}

interface IProps {
  hasBackButton?: boolean;
  question: IntlMsgShape | string;
  section: {
    sectionName: string;
    choices: Choice[];
  };
  tracking: {
    name: string;
    url: string;
  };
}

const StepWithOptions = ({
  hasBackButton = true,
  question,
  section,
  tracking,
}: IProps) => {
  const { nextStep, prevStep, setQuiz, responses, currentStep, totalSteps } =
    useQuizContext();
  const { formatMessage } = useIntl();

  document.body.style.backgroundColor = '#f3e8e3'; // light-body;

  const { sectionName, choices } = section;

  /*
   *  TODO: create an option for the Orchestrator to configure the number of steps that are part of the counter.
   *  For example, if there are a total 5 steps, but only 3 steps counts for the progress bar. */
  const progressTotalSteps = totalSteps - 2;

  const getValue = (cardId: number) => {
    return choices.find((choice) => choice.id === cardId);
  };

  const handleNextStep = (cardId: number) => {
    if (cardId && sectionName) {
      const value = getValue(cardId);

      setQuiz({
        stepName: sectionName,
        answer: {
          id: cardId,
          text: formatMessage({ id: value?.text }),
          tracking: value?.tracking,
        },
      });

      tracker.sendEvent(
        tracking.name,
        [
          {
            section: sectionName,
            'selected-options': value?.tracking,
          },
        ],
        !isProduction(),
        [dataLayerProvider],
      );

      nextStep();
    }
  };

  const isSelected = (cardId: number) => {
    const response = responses[sectionName];

    if (!response) {
      return false;
    }

    return cardId === response.answer.id;
  };

  return (
    <StepPageViewWrapper trackingUrl={tracking.url}>
      <ProgressBar
        currentStep={currentStep + 1}
        totalSteps={progressTotalSteps}
      />
      <div className="Step">
        <Typography variant="h2" centered color="dark-body">
          <Message message={question} />
        </Typography>

        <div className="Step__cards">
          {choices.map((choice) => {
            return (
              <QuizCard
                key={choice.id}
                cardId={choice.id}
                onCardClick={() => {
                  handleNextStep(choice.id);
                }}
                selected={isSelected(choice.id)}
                config={{
                  textColor: 'dark',
                }}
              >
                <Typography variant="h3" color="dark-body">
                  <Message
                    message={{
                      id: choice.text,
                    }}
                  />
                </Typography>
              </QuizCard>
            );
          })}
        </div>

        {hasBackButton && <StepsBackButton onClick={() => prevStep()} />}
      </div>
    </StepPageViewWrapper>
  );
};

export default StepWithOptions;
